/**
 * SNotes AI Bot(챗봇) ↔ 메뉴디자이너 연결 브리지.
 *
 * 메뉴디자이너 탭이 마운트되면 컨텍스트(이슈번호/담당자 + 화면 적용 콜백)를 등록하고,
 * 챗봇은 메뉴디자이너 탭이 활성일 때 입력된 명령을 이 브리지로 보낸다.
 *
 * 새 계약(snotes-ai aiStudio)에서는 업무 API가 나뉘어 있지 않다. 화면 설계·Query 설정·
 * 임시저장·배포는 모두 Agent가 Tool을 호출해 처리하므로, 이 브리지가 하는 일은
 *   ① 사용자 명령을 그대로 Agent에 전달 (POST /snotes/aiStudio)
 *   ② conversationId 보관 — 2턴부터 직전 응답 값을 그대로 회신
 *   ③ 돌아온 draft를 편집기에 적용
 *   ④ 배포는 되묻고 나서 명시적 확인을 받은 뒤에만 Agent에 지시
 * 뿐이다. HTTP 계약은 studioAiApi에, UI는 챗봇에 맡긴다(이 파일은 fetch를 직접 하지 않는다).
 *
 * 임시저장도 Agent가 Tool로 처리하므로 별도 버튼/API가 없다 — 사용자가 챗봇에
 * "임시저장해줘"라고 말하면 그대로 Agent에게 전달된다.
 *
 * 챗봇과는 @shared/libs/chatCommandBridge(범용 핸들러 레지스트리)를 통해 연결된다.
 * 챗봇이 이 파일을 직접 import하지 않으므로 의존은 (디자이너 → shared) 한 방향이다.
 */
import {
  registerChatCommandHandler,
  unregisterChatCommandHandler,
  type ChatCommandHandler,
} from "@shared/libs/chatCommandBridge";
import { studioAiActions } from "@shared/libs/studioAiActions";
import { askStudioAi, type DraftQueries, type StudioSpec } from "./studioAiApi";
import { describeError } from "./errorText";
import type { StudioModel } from "./studioTypes";

export const DESIGNER_CD_MENU = "SN_SFA_ISSUE_REG_006";

export interface StudioAiBridgeContext {
  /** 이슈번호 → noIssue (저장·배포 시 새 화면에 스탬프될 cd_menu) */
  issueNo?: string;
  /** 담당자사번 — 저장·배포에 필요 */
  cdEmp?: string;
  /**
   * 이번 턴의 화면 초안을 편집기에 적용.
   * draftQueries(그리드별 조회쿼리 초안)는 draft 안이 아니라 응답 형제 필드로 오므로
   * draft→미리보기 메타 변환과 분리해서 함께 넘긴다.
   */
  applyScreen: (
    model: StudioModel,
    draft: StudioSpec,
    draftQueries: DraftQueries
  ) => void;
  /** 새 채팅 → 편집 중인 화면을 버리고 랜딩(빈 상태)으로 되돌린다 */
  onNewChat: () => void;
}

let ctx: StudioAiBridgeContext | null = null;
let conversationId: string | null = null;

/** 배포 확인 대기 중인지 — "저장해줘" 다음 턴에 "배포 확정"을 받아야 Agent에 배포를 지시한다 */
let awaitingDeployConfirm = false;

/**
 * 배포 의도로 볼 문장. 예전에는 /저장|배포/ 부분일치였는데, 그러면
 * "저장하지 마", "저장 전에 컬럼 하나만 더 추가해줘" 같은 문장까지 배포를 실행했다.
 * 배포는 기존 sys_formctrl 행을 지우고 덮어쓰는 비가역 작업이라 문장 전체를 본다.
 */
const DEPLOY_INTENT =
  /^(이\s*|지금\s*|현재\s*)?(화면\s*)?(저장|배포)\s*(해줘|해|해라|하기|할래|해주세요|부탁해|부탁드려요)?\s*[.!]?$/;

/** 확인 단계 응답 */
const DEPLOY_CONFIRM = /^(배포|저장)\s*확정\s*[.!]?$/;

/**
 * 확인을 받은 뒤 Agent에게 보낼 배포 지시문.
 * snotes-ai도 명시적인 배포 지시를 요구하므로, 확인 문답의 결과를 자연어 명령으로 바꿔
 * 그대로 전달한다(프론트가 배포 API를 직접 호출하지 않는다).
 */
const DEPLOY_COMMAND = "지금 편집 중인 화면을 배포해줘.";

/** 챗봇 레지스트리에 등록할 핸들러 (동일 참조여야 해제가 되므로 모듈 상수로 둔다) */
const chatHandler: ChatCommandHandler = {
  cdMenu: DESIGNER_CD_MENU,
  handle: (command) => sendStudioAiCommand(command),
};

export function registerStudioAiBridge(c: StudioAiBridgeContext) {
  // 다른 컨텍스트(다른 이슈/담당자)로 교체되는 경우 이전 대화를 물려주면 안 된다
  if (ctx && ctx !== c) resetStudioAiConversation();
  ctx = c;
  registerChatCommandHandler(chatHandler);
  // 챗봇 사이드바의 '새 채팅'이 호출할 실행기 등록
  studioAiActions.getState().setActions({ newChat });
}

export function unregisterStudioAiBridge(c: StudioAiBridgeContext) {
  // 탭이 닫히면 대화도 끝난다. conversationId를 남겨두면 다른 이슈로 디자이너를
  // 다시 열었을 때 이전 화면의 대화가 이어져, 새 화면을 만들라는 명령이
  // 엉뚱한 이전 화면 수정으로 처리된다.
  if (ctx === c) {
    ctx = null;
    resetStudioAiConversation();
    unregisterChatCommandHandler(chatHandler);
    studioAiActions.getState().setActions(null);
  }
}

/**
 * 새 채팅 — 진행 중인 대화를 끊고 편집 중인 화면도 버린다.
 *
 * 대화만 리셋하고 화면을 남겨두면, 새 대화의 첫 명령이 "이 화면을 고쳐줘"로 읽히는 것처럼
 * 보이지만 서버는 아무 맥락도 없는 상태라 결과가 어긋난다. 둘을 함께 비운다.
 */
function newChat() {
  resetStudioAiConversation();
  ctx?.onNewChat();
}

/** 새 대화 시작 (새 화면 작업 시) */
export function resetStudioAiConversation() {
  conversationId = null;
  awaitingDeployConfirm = false;
}

/** 챗봇에서 온 명령 처리. 챗봇에 표시할 답변 텍스트를 반환 (실패도 텍스트로) */
export async function sendStudioAiCommand(command: string): Promise<string> {
  if (!ctx) return "메뉴디자이너 화면이 열려 있지 않습니다.";
  const c = command.trim();

  // 1) 배포 의도 → 바로 넘기지 않고 확인을 받는다 (기존 화면 설정을 덮어쓰므로)
  if (!awaitingDeployConfirm && DEPLOY_INTENT.test(c)) {
    if (!conversationId) {
      return "아직 배포할 화면이 없습니다. 먼저 만들고 싶은 화면을 말씀해 주세요. (예: 발주현황을 참고해서 이슈등록 화면 만들어줘)";
    }
    awaitingDeployConfirm = true;
    return (
      `배포하면 이 화면의 기존 검색조건/그리드 설정을 지우고 현재 편집 중인 내용으로 덮어씁니다.\n` +
      `계속하려면 "배포 확정"이라고 입력해 주세요. (취소하려면 다른 명령을 이어서 말씀하시면 됩니다)`
    );
  }

  // 2) 확인 단계 응답 → Agent에게 배포를 지시한다 (배포 Tool은 Agent가 호출)
  if (awaitingDeployConfirm) {
    const confirmed = DEPLOY_CONFIRM.test(c);
    awaitingDeployConfirm = false;
    // 확인이 아니면 방금 입력한 명령을 그대로 이어서 처리한다(= 배포 취소)
    return askAgent(confirmed ? DEPLOY_COMMAND : c);
  }

  // 3) 일반 명령
  return askAgent(c);
}

/**
 * Agent 호출 1회 — 명령 전달 → conversationId 갱신 → draft 화면 적용.
 * 무엇을 할지(설계/쿼리/임시저장/배포)는 Agent가 정하므로 프론트는 분기하지 않는다.
 */
async function askAgent(command: string): Promise<string> {
  if (!ctx) return "메뉴디자이너 화면이 열려 있지 않습니다.";
  try {
    const res = await askStudioAi(command, {
      conversationId: conversationId ?? undefined,
      noIssue: ctx.issueNo,
      cdEmp: ctx.cdEmp,
    });
    // 서버가 준 값만 쓴다 — 프론트에서 대화 ID를 만들거나 바꾸지 않는다
    if (res.conversationId) conversationId = res.conversationId;
    if (res.model && res.draft) {
      ctx.applyScreen(res.model, res.draft, res.draftQueries);
    }
    return res.message;
  } catch (e) {
    return describeError(e);
  }
}
